import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../services/api';

interface EmbedData {
  id: string;
  public_id: string;
  is_active: boolean;
  view_count: number;
  join_count: number;
  customization: string;
  allowed_domains: string;
  topic: string;
  start_time: string;
  zoom_meeting_id: string;
  join_url: string;
}

export default function EmbedPage() {
  const { id } = useParams<{ id: string }>();
  const [embed, setEmbed] = useState<EmbedData | null>(null);
  const [embedCode, setEmbedCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);

  // Customization state
  const [customization, setCustomization] = useState({
    primaryColor: '#2563eb',
    width: '100%',
    height: '500px',
    showTitle: true,
  });
  const [allowedDomains, setAllowedDomains] = useState('');

  useEffect(() => {
    loadEmbed();
  }, [id]);

  const loadEmbed = async () => {
    try {
      setLoading(true);
      const response = await api.getEmbed(id!);
      setEmbed(response.embed);
      setEmbedCode(response.embed_code);

      // Parse existing customization
      if (response.embed.customization) {
        try {
          const parsed = JSON.parse(response.embed.customization);
          setCustomization(prev => ({ ...prev, ...parsed }));
        } catch {}
      }

      // Parse allowed domains
      if (response.embed.allowed_domains) {
        try {
          const domains = JSON.parse(response.embed.allowed_domains);
          setAllowedDomains(domains.join('\n'));
        } catch {}
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load embed');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSaveCustomization = async () => {
    try {
      setSaving(true);
      const domains = allowedDomains
        .split('\n')
        .map(d => d.trim())
        .filter(d => d.length > 0);

      const response = await api.updateEmbed(id!, {
        customization,
        allowed_domains: domains,
      });

      setEmbed(response.embed);
      setEmbedCode(response.embed_code);
      alert('Customization saved!');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save customization');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async () => {
    try {
      const response = await api.updateEmbed(id!, {
        is_active: !embed?.is_active,
      });
      setEmbed(response.embed);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update embed status');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (error || !embed) {
    return (
      <div className="min-h-screen bg-gray-50">
        <nav className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <Link to="/dashboard" className="text-2xl font-bold text-indigo-600">EmbedZoom</Link>
          </div>
        </nav>
        <main className="max-w-2xl mx-auto px-6 py-12 text-center">
          <h1 className="text-2xl font-bold mb-4 text-red-600">Error</h1>
          <p className="text-gray-600 mb-6">{error || 'Embed not found'}</p>
          <Link to="/meetings" className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
            Back to Meetings
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/dashboard" className="text-2xl font-bold text-indigo-600">EmbedZoom</Link>
          <Link to="/meetings" className="text-gray-600 hover:text-gray-800">
            Back to Meetings
          </Link>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Embed Code</h1>
            <p className="text-gray-600">{embed.topic}</p>
          </div>
          <div className="flex items-center space-x-4">
            <span className={`px-3 py-1 rounded-full text-sm ${
              embed.is_active
                ? 'bg-green-100 text-green-600'
                : 'bg-red-100 text-red-600'
            }`}>
              {embed.is_active ? 'Active' : 'Inactive'}
            </span>
            <button
              onClick={handleToggleActive}
              className="text-sm text-gray-600 hover:text-gray-800"
            >
              {embed.is_active ? 'Deactivate' : 'Activate'}
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-white p-4 rounded-lg shadow text-center">
            <p className="text-2xl font-bold text-indigo-600">{embed.view_count}</p>
            <p className="text-sm text-gray-500">Views</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow text-center">
            <p className="text-2xl font-bold text-green-600">{embed.join_count}</p>
            <p className="text-sm text-gray-500">Joins</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow text-center">
            <p className="text-2xl font-bold text-blue-600">
              {embed.view_count > 0
                ? Math.round((embed.join_count / embed.view_count) * 100)
                : 0}%
            </p>
            <p className="text-sm text-gray-500">Conversion</p>
          </div>
        </div>

        {/* Embed Code */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Embed Code</h2>
            <button
              onClick={handleCopyCode}
              className={`px-4 py-2 rounded-lg transition-colors ${
                copied
                  ? 'bg-green-600 text-white'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              }`}
            >
              {copied ? 'Copied!' : 'Copy Code'}
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Copy and paste this code into your website where you want the meeting to appear.
          </p>
          <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto text-sm">
            <code>{embedCode}</code>
          </pre>
        </div>

        {/* Customization */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4">Customization</h2>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Primary Color
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="color"
                  value={customization.primaryColor}
                  onChange={(e) => setCustomization({ ...customization, primaryColor: e.target.value })}
                  className="w-12 h-10 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={customization.primaryColor}
                  onChange={(e) => setCustomization({ ...customization, primaryColor: e.target.value })}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Width
              </label>
              <input
                type="text"
                value={customization.width}
                onChange={(e) => setCustomization({ ...customization, width: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="100% or 800px"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minimum Height
              </label>
              <input
                type="text"
                value={customization.height}
                onChange={(e) => setCustomization({ ...customization, height: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="500px"
              />
              <p className="text-xs text-gray-500 mt-1">
                Recommended: 500px. The widget will expand as needed for gallery view.
              </p>
            </div>

            <div className="flex items-center">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={customization.showTitle}
                  onChange={(e) => setCustomization({ ...customization, showTitle: e.target.checked })}
                  className="w-4 h-4 text-indigo-600 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Show Meeting Title</span>
              </label>
            </div>
          </div>
        </div>

        {/* Domain Whitelist */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4">Domain Whitelist</h2>
          <p className="text-sm text-gray-600 mb-4">
            Restrict which domains can embed this meeting. Leave empty to allow all domains.
            Enter one domain per line (e.g., example.com).
          </p>
          <textarea
            value={allowedDomains}
            onChange={(e) => setAllowedDomains(e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
            placeholder="example.com&#10;mywebsite.com"
          />
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSaveCustomization}
            disabled={saving}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        {/* Meeting Details */}
        <div className="bg-white rounded-lg shadow p-6 mt-8">
          <h2 className="text-xl font-semibold mb-4">Meeting Details</h2>
          <dl className="grid grid-cols-2 gap-4">
            <div>
              <dt className="text-sm text-gray-500">Topic</dt>
              <dd className="font-medium">{embed.topic}</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-500">Start Time</dt>
              <dd className="font-medium">{formatDate(embed.start_time)}</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-500">Zoom Meeting ID</dt>
              <dd className="font-medium">{embed.zoom_meeting_id}</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-500">Public Embed ID</dt>
              <dd className="font-medium font-mono text-sm">{embed.public_id}</dd>
            </div>
          </dl>
          <div className="mt-4">
            <a
              href={embed.join_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 hover:text-indigo-700"
            >
              Open in Zoom
            </a>
          </div>
        </div>
      </main>
    </div>
  );
}
